'use client';

import React from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { isPublishV1Platform, type PublishV1Platform } from '@/lib/publish/publishScope';
import { labelForPublishPlatform } from '@/lib/publish/publishTargets';

type DiagnosticRow = {
  platform: string;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastFailureDetail: string | null;
  updatedAt: string;
};

function platformLabel(p: string): string {
  if (isPublishV1Platform(p)) return labelForPublishPlatform(p as PublishV1Platform);
  return p;
}

function fmt(at: string | null): string {
  if (!at) return '—';
  try {
    return new Date(at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

/** Hub 1.4 — self-serve “last publish attempt” hints from composer session data (not vendor dashboards). */
export function NativePublishDiagnosticsCard() {
  const [rows, setRows] = React.useState<DiagnosticRow[]>([]);
  const [err, setErr] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetch('/api/user/native-publish-diagnostics', { credentials: 'include' });
        const j = (await r.json().catch(() => ({}))) as {
          diagnostics?: DiagnosticRow[];
          error?: string;
        };
        if (!r.ok) {
          if (!cancelled) setErr(j.error ?? 'Could not load diagnostics.');
          return;
        }
        if (!cancelled) setRows(j.diagnostics ?? []);
      } catch {
        if (!cancelled) setErr('Could not load diagnostics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Last native publish (diagnostics)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Updated when you publish from Create or Media Library. Use this before contacting support if a
          post failed on one network.
        </Typography>
        {err ? (
          <Typography variant="body2" color="error">
            {err}
          </Typography>
        ) : loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No publish attempts recorded from FloAI yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Platform</TableCell>
                <TableCell>Last success</TableCell>
                <TableCell>Last failure</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.platform}>
                  <TableCell>{platformLabel(r.platform)}</TableCell>
                  <TableCell>{fmt(r.lastSuccessAt)}</TableCell>
                  <TableCell>{fmt(r.lastFailureAt)}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    {r.lastFailureDetail ? (
                      <Typography variant="caption" color="text.secondary" component="span" noWrap={false}>
                        {r.lastFailureDetail.slice(0, 240)}
                        {r.lastFailureDetail.length > 240 ? '…' : ''}
                      </Typography>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
